import React, { useState, useRef, useEffect } from 'react';
import domtoimage from 'dom-to-image';
import './PlotAIChat.css';

const UnifiedAIChat = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [attachedImage, setAttachedImage] = useState(null);
    const [capturingImage, setCapturingImage] = useState(null); // Now tracks which button: 'stackplot', 'ion', 'electron', 'slice', or null
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);
    const [tooltips, setTooltips] = useState({
        stackplot: 'Stackplot',
        ion: '1: Ion VDF',
        electron: '2: Electron VDF',
        slice: '3: Slice VDF'
    });
    const messagesEndRef = useRef(null);
    const hasInitialized = useRef(false);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, loading, capturingImage]);

    // Format axis labels with proper symbols and subscripts
    const formatAxisLabel = (text) => {
        if (!text) return '';
        
        let label = text.replace(/<[^>]*>/g, '').split('(')[0].trim();
        
        label = label.replace(/\bVV\s+perp\s+B\b/gi, 'V⊥B')
                    .replace(/\bVV\s+X\s+B\b/gi, 'V×B')
                    .replace(/\bV\s+perp\s+B\b/gi, 'V⊥B')
                    .replace(/\bV\s+X\s+B\b/gi, 'V×B')
                    .replace(/\bperp\b/gi, '⊥')
                    .replace(/\sX\s/g, '×')
                    .replace(/\bVV\s+L\b/g, 'Vₗ')
                    .replace(/\bVV\s+M\b/g, 'Vₘ')
                    .replace(/\bVV\s+N\b/g, 'Vₙ')
                    .replace(/\bVV\s+x\b/g, 'Vₓ')
                    .replace(/\bVV\s+y\b/g, 'Vᵧ')
                    .replace(/\bVV\s+z\b/g, 'Vz')
                    .replace(/\bV\s+L\b/g, 'Vₗ')
                    .replace(/\bV\s+M\b/g, 'Vₘ')
                    .replace(/\bV\s+N\b/g, 'Vₙ')
                    .replace(/\bV\s+x\b/g, 'Vₓ')
                    .replace(/\bV\s+y\b/g, 'Vᵧ')
                    .replace(/\bV\s+z\b/g, 'Vz')
                    .replace(/\bVX\b/g, 'Vₓ')
                    .replace(/\bVY\b/g, 'Vᵧ')
                    .replace(/\bVZ\b/g, 'Vz')
                    .replace(/\bVx\b/g, 'Vₓ')
                    .replace(/\bVy\b/g, 'Vᵧ')
                    .replace(/\bVz\b/g, 'Vz');
        
        return label;
    };

    // Update tooltips when plots are available
    useEffect(() => {
        const updateTooltips = () => {
            const plotConfigs = [
                { prefix: 'ion', key: 'ion', label: '1' },
                { prefix: 'ele', key: 'electron', label: '2' },
                { prefix: 'sl3', key: 'slice', label: '3' }
            ];
            
            const newTooltips = { stackplot: 'Stackplot' };
            
            plotConfigs.forEach(({ prefix, key, label }) => {
                const plotElement = findVisiblePlot(prefix);
                if (plotElement) {
                    try {
                        const layout = plotElement.layout;
                        
                        if (layout && layout.xaxis && layout.yaxis) {
                            const xAxisText = layout.xaxis.title?.text || '';
                            const yAxisText = layout.yaxis.title?.text || '';
                            
                            const xLabel = formatAxisLabel(xAxisText);
                            const yLabel = formatAxisLabel(yAxisText);
                            
                            if (xLabel && yLabel) {
                                newTooltips[key] = `${label}: X: ${xLabel}, Y: ${yLabel}`;
                            } else {
                                newTooltips[key] = `${label}:`;
                            }
                        } else {
                            newTooltips[key] = `${label}:`;
                        }
                    } catch (e) {
                        console.error('Error extracting tooltip for', prefix, e);
                        newTooltips[key] = `${label}:`;
                    }
                } else {
                    newTooltips[key] = `${label}:`;
                }
            });
            
            setTooltips(newTooltips);
        };
        
        if (isOpen) {
            updateTooltips();
            const timer = setInterval(updateTooltips, 2000);
            return () => clearInterval(timer);
        }
    }, [isOpen]);

    // Auto-capture stackplot and set default question when first opened
    useEffect(() => {
        if (isOpen && messages.length === 0 && !hasInitialized.current) {
            hasInitialized.current = true;
            // Capture the stackplot automatically
            const plotEl = document.getElementById('plotly-results');
            if (plotEl) {
                setCapturingImage('stackplot');
                // Small delay to ensure the hourglass renders before capture starts
                setTimeout(() => {
                    domtoimage.toPng(plotEl, { bgcolor: '#ffffff' })
                        .then(dataUrl => {
                            setAttachedImage(dataUrl);
                            setCapturingImage(null);
                        })
                        .catch(e => {
                            setError('Could not capture plot: ' + e.message);
                            setCapturingImage(null);
                        });
                }, 50);
            }
            // Set a default question
            setInput('Please analyze this plot and describe any notable features or patterns you observe.');
        }
    }, [isOpen, messages.length]);

    const findVisiblePlot = (prefix) => {
        for (let i = 0; i < 20; i++) {
            const id = `${prefix}-dr${i}`;
            const elem = document.getElementById(id);
            if (elem && elem.style.visibility !== 'hidden' && elem.style.display !== 'none') {
                return elem;
            }
        }
        return document.getElementById(`${prefix}-dr0`);
    };

    const captureStackplot = async () => {
        const plotEl = document.getElementById('plotly-results');
        if (!plotEl) {
            setError('Stackplot not found — generate a plot first.');
            return;
        }
        setCapturingImage('stackplot');
        setError(null);
        // Small delay to ensure the hourglass renders before capture starts
        await new Promise(resolve => setTimeout(resolve, 50));
        try {
            const dataUrl = await domtoimage.toPng(plotEl, { bgcolor: '#ffffff' });
            setAttachedImage(dataUrl);
        } catch (e) {
            setError('Could not capture stackplot: ' + e.message);
        } finally {
            setCapturingImage(null);
        }
    };

    const captureVDF = async (plotType) => {
        let plotElement;
        let plotLabel;
        
        if (plotType === 'ion') {
            plotElement = findVisiblePlot('ion');
            plotLabel = 'Ion';
        } else if (plotType === 'electron') {
            plotElement = findVisiblePlot('ele');
            plotLabel = 'Electron';
        } else if (plotType === 'slice') {
            plotElement = findVisiblePlot('sl3');
            plotLabel = 'Slice';
        }
        
        if (!plotElement) {
            setError(`${plotLabel} VDF plot not found — generate plots first.`);
            return;
        }
        
        setCapturingImage(plotType);
        setError(null);
        // Small delay to ensure the hourglass renders before capture starts
        await new Promise(resolve => setTimeout(resolve, 50));
        try {
            const dataUrl = await domtoimage.toPng(plotElement, { bgcolor: '#ffffff' });
            setAttachedImage(dataUrl);
        } catch (e) {
            setError(`Could not capture ${plotLabel} VDF: ` + e.message);
        } finally {
            setCapturingImage(null);
        }
    };

    const removeAttachedImage = () => {
        setAttachedImage(null);
    };

    const sendMessage = async () => {
        const text = input.trim();
        if (!text || loading) return;

        const userMsg = {
            role: 'user',
            content: text,
            imageDataUrl: attachedImage || null,
        };
        const updatedMessages = [...messages, userMsg];
        setMessages(updatedMessages);
        setInput('');
        setAttachedImage(null);
        setLoading(true);
        setError(null);

        const history = messages.map(m => ({ role: m.role, content: m.content }));

        try {
            const response = await fetch('/api/chat_with_plot', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: text,
                    history,
                    image_base64: userMsg.imageDataUrl || null,
                }),
            });

            if (!response.ok) {
                throw new Error(`Server error: ${response.status}`);
            }

            const data = await response.json();

            if (data.error) {
                setError(data.error);
            } else {
                setMessages(prev => [
                    ...prev,
                    { role: 'assistant', content: data.response },
                ]);
            }
        } catch (e) {
            setError(e.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const clearConversation = () => {
        setMessages([]);
        setAttachedImage(null);
        setError(null);
        hasInitialized.current = false;
    };

    const copyConversation = async () => {
        if (messages.length === 0) return;
        
        const text = messages.map(msg => {
            const label = msg.role === 'user' ? 'You' : 'AI';
            const imageNote = msg.imageDataUrl ? ' [Image attached]' : '';
            return `${label}:${imageNote}\n${msg.content}\n`;
        }).join('\n');
        
        const html = messages.map(msg => {
            const label = msg.role === 'user' ? 'You' : 'AI';
            const labelStyle = msg.role === 'user' 
                ? 'color: #2c5282; font-weight: bold; margin-top: 12px;' 
                : 'color: #2d3748; font-weight: bold; margin-top: 12px;';
            
            let imageHtml = '';
            if (msg.imageDataUrl) {
                imageHtml = `<div style="margin: 8px 0;"><img src="${msg.imageDataUrl}" style="max-width: 400px; border: 1px solid #e2e8f0; border-radius: 4px;" /></div>`;
            }
            
            const contentHtml = msg.content.split('\n').map(line => 
                `<p style="margin: 4px 0;">${line}</p>`
            ).join('');
            
            return `<div style="${labelStyle}">${label}:</div>${imageHtml}<div style="margin-bottom: 16px;">${contentHtml}</div>`;
        }).join('');
        
        try {
            const clipboardItem = new ClipboardItem({
                'text/plain': new Blob([text], { type: 'text/plain' }),
                'text/html': new Blob([html], { type: 'text/html' })
            });
            await navigator.clipboard.write([clipboardItem]);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <div className="plot-ai-chat-wrapper">
            <button
                className={'plot-ai-chat-toggle' + (isOpen ? ' open' : '')}
                onClick={() => setIsOpen(o => !o)}
            >
                <span className="plot-ai-chat-toggle-icon">✦</span>
                {isOpen ? 'Hide AI Chat' : 'Ask AI about plots'}
            </button>

            {isOpen && (
                <div className="plot-ai-chat-panel">
                    <div className="plot-ai-chat-header">
                        <span>AI Chat</span>
                        <div className="plot-ai-chat-header-actions">
                            {messages.length > 0 && (
                                <>
                                    <button className="plot-ai-copy-btn" onClick={copyConversation}>
                                        {copied ? '✓ Copied' : 'Copy'}
                                    </button>
                                    <button className="plot-ai-clear-btn" onClick={clearConversation}>
                                        Clear
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="plot-ai-chat-messages">
                        {messages.length === 0 && (
                            <div className="plot-ai-chat-empty">
                                Capture the stackplot or any VDF plot and ask the AI about features, anomalies, or comparisons.
                            </div>
                        )}
                        {messages.map((msg, i) => (
                            <div key={i} className={'plot-ai-msg plot-ai-msg-' + msg.role}>
                                <div className="plot-ai-msg-label">
                                    {msg.role === 'user' ? 'You' : 'AI'}
                                </div>
                                {msg.imageDataUrl && (
                                    <img
                                        src={msg.imageDataUrl}
                                        alt="Attached plot"
                                        className="plot-ai-msg-image"
                                    />
                                )}
                                <div className="plot-ai-msg-content">
                                    {msg.content}
                                </div>
                            </div>
                        ))}
                        {capturingImage !== null && (
                            <div className="plot-ai-msg plot-ai-msg-assistant plot-ai-thinking">
                                <div className="plot-ai-msg-label">Capturing</div>
                                <div className="plot-ai-msg-content" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontSize: '1.5em' }}>⏳</span>
                                    <span>Processing plot image...</span>
                                </div>
                            </div>
                        )}
                        {loading && (
                            <div className="plot-ai-msg plot-ai-msg-assistant plot-ai-thinking">
                                <div className="plot-ai-msg-label">AI</div>
                                <div className="plot-ai-msg-content plot-ai-dots">
                                    <span /><span /><span />
                                </div>
                            </div>
                        )}
                        {error && (
                            <div className="plot-ai-error">{error}</div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {attachedImage && (
                        <div className="plot-ai-attached-preview">
                            <img src={attachedImage} alt="Plot to send" className="plot-ai-attached-thumb" />
                            <button className="plot-ai-remove-img" onClick={removeAttachedImage} title="Remove image">✕</button>
                        </div>
                    )}

                    <div className="plot-ai-chat-input-row">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <button
                                className={'plot-ai-capture-btn' + (capturingImage === 'stackplot' ? ' capturing' : '') + (attachedImage ? ' attached' : '')}
                                onClick={captureStackplot}
                                disabled={capturingImage !== null || loading}
                                title={tooltips.stackplot}
                                style={{ fontSize: '0.7em', padding: '4px 8px' }}
                            >
                                {capturingImage === 'stackplot' ? '⏳' : attachedImage ? '✓' : '📊'}
                            </button>
                            <button
                                className={'plot-ai-capture-btn' + (capturingImage === 'ion' ? ' capturing' : '') + (attachedImage ? ' attached' : '')}
                                onClick={() => captureVDF('ion')}
                                disabled={capturingImage !== null || loading}
                                title={tooltips.ion}
                                style={{ fontSize: '0.7em', padding: '4px 8px' }}
                            >
                                {capturingImage === 'ion' ? '⏳' : attachedImage ? '✓' : ''} 1
                            </button>
                            <button
                                className={'plot-ai-capture-btn' + (capturingImage === 'electron' ? ' capturing' : '') + (attachedImage ? ' attached' : '')}
                                onClick={() => captureVDF('electron')}
                                disabled={capturingImage !== null || loading}
                                title={tooltips.electron}
                                style={{ fontSize: '0.7em', padding: '4px 8px' }}
                            >
                                {capturingImage === 'electron' ? '⏳' : attachedImage ? '✓' : ''} 2
                            </button>
                            <button
                                className={'plot-ai-capture-btn' + (capturingImage === 'slice' ? ' capturing' : '') + (attachedImage ? ' attached' : '')}
                                onClick={() => captureVDF('slice')}
                                disabled={capturingImage !== null || loading}
                                title={tooltips.slice}
                                style={{ fontSize: '0.7em', padding: '4px 8px' }}
                            >
                                {capturingImage === 'slice' ? '⏳' : attachedImage ? '✓' : ''} 3
                            </button>
                        </div>
                        <textarea
                            className="plot-ai-chat-input"
                            rows={2}
                            placeholder="Ask about the plots… (Enter to send, Shift+Enter for newline)"
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={loading}
                        />
                        <button
                            className="plot-ai-send-btn"
                            onClick={sendMessage}
                            disabled={loading || !input.trim()}
                        >
                            Send
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UnifiedAIChat;
